import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Slider } from './ui/slider';
import { Star, X, Plus, Trash2, Save, RotateCcw, Download, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import bggService from '../services/bggService';

const GameEditModal = memo(({ game, isOpen, onClose, onSave, onDelete, isAuthenticated = true }) => {
  const [editedGame, setEditedGame] = useState({});
  const [newTag, setNewTag] = useState('');
  const [isChanged, setIsChanged] = useState(false);
  const [bggUrl, setBggUrl] = useState('');
  const [isLoadingBGG, setIsLoadingBGG] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const { toast } = useToast();

  // Helper function to check if game has co-op tags
  const hasCoOpTags = (tags = []) => {
    if (!Array.isArray(tags)) return false;
    const coopKeywords = ['cooperative', 'co-op', 'cooperative game'];
    return tags.some(tag => 
      coopKeywords.some(keyword => 
        tag.toLowerCase().includes(keyword)
      )
    );
  };

  // Extract BGG ID from various URL formats
  const extractBGGId = (input) => {
    if (!input) return null;
    
    const trimmed = input.trim();
    
    // Check if it's just a number (game ID)
    if (/^\d+$/.test(trimmed)) {
      return trimmed;
    }
    
    // Extract ID from BGG URLs
    const patterns = [
      /boardgamegeek\.com\/boardgame\/(\d+)/i,
      /bgg\.cc\/(\d+)/i,
      /\/boardgame\/(\d+)/i
    ];
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // Fetch BGG data and populate form
  const handleFetchBGGData = async () => {
    // Check authentication before allowing BGG fetch
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please enter PIN to access BoardGameGeek data fetching",
        variant: "destructive",
      });
      return;
    }

    if (!bggUrl.trim()) {
      toast({
        title: "BGG URL Required",
        description: "Please enter a BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    const bggId = extractBGGId(bggUrl);
    if (!bggId) {
      toast({
        title: "Invalid BGG URL",
        description: "Please enter a valid BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingBGG(true);
    
    try {
      console.log('Fetching BGG data for ID:', bggId);
      
      const bggData = await bggService.getGameDetails(bggId);
      
      if (!bggData) {
        toast({
          title: "BGG Data Not Found",
          description: "Could not find game data on BoardGameGeek. Please check the ID/URL and try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('BGG data received:', bggData);
      console.log('Extracted BGG ID:', bggId);

      // Update form with BGG data while preserving existing status
      const updatedGame = {
        ...editedGame,
        // Update core game info from BGG
        name: bggData.name || editedGame.name,
        description: bggData.description || editedGame.description,
        image: bggData.image || editedGame.image,
        yearPublished: bggData.yearPublished || editedGame.yearPublished,
        minPlayers: bggData.players?.min || editedGame.minPlayers,
        maxPlayers: bggData.players?.max || editedGame.maxPlayers,
        playtime: bggData.playtime || editedGame.playtime,
        complexity: bggData.complexity || editedGame.complexity,
        rating: bggData.rating || editedGame.rating,
        bggId: bggId, // Use the extracted BGG ID directly
        bggEnriched: true,
        // Merge tags (combine existing with BGG tags, remove duplicates)
        tags: bggData.tags ? 
          [...new Set([...(editedGame.tags || []), ...bggData.tags])] : 
          editedGame.tags,
        // PRESERVE existing status - do not override these
        owned: editedGame.owned,
        wishlist: editedGame.wishlist,
        try: editedGame.try,
        isCoOp: editedGame.isCoOp // Also preserve co-op status
      };

      setEditedGame(updatedGame);
      setIsChanged(true);
      setBggUrl(''); // Clear the input

      toast({
        title: "BGG Data Loaded",
        description: `Successfully updated "${bggData.name}" with BoardGameGeek data`,
      });

    } catch (error) {
      console.error('Error fetching BGG data:', error);
      toast({
        title: "BGG Fetch Failed",
        description: "Failed to fetch data from BoardGameGeek. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBGG(false);
    }
  };

  useEffect(() => {
    if (game && isOpen) {
      // Check if game should be marked as co-op based on either field or tags
      const shouldBeCoOp = game.isCoOp || hasCoOpTags(game.tags);
      
      setEditedGame({
        ...game,
        minPlayers: game.players?.min || 1,
        maxPlayers: game.players?.max || 4,
        tags: [...(game.tags || [])],
        isCoOp: shouldBeCoOp // Set checkbox based on field OR tags
      });
      setIsChanged(false);
      
      // Set image preview
      if (game.image && game.image.startsWith('data:')) {
        setImagePreview(game.image);
      } else {
        setImagePreview('');
      }
    }
  }, [game, isOpen]);

  const handleChange = useCallback((field, value) => {
    setEditedGame(prev => ({
      ...prev,
      [field]: value
    }));
    setIsChanged(true);
  }, []);

  const handleAddTag = () => {
    if (newTag.trim() && !editedGame.tags.includes(newTag.trim())) {
      const updatedTags = [...editedGame.tags, newTag.trim()];
      handleChange('tags', updatedTags);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const updatedTags = editedGame.tags.filter(tag => tag !== tagToRemove);
    handleChange('tags', updatedTags);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Image must be smaller than 2MB",
        variant: "destructive",
      });
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a JPEG, PNG, WebP, or GIF image",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingImage(true);
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setImagePreview(base64Image);
        handleChange('image', base64Image);
        setIsUploadingImage(false);
        
        toast({
          title: "Image Uploaded",
          description: "Image has been uploaded successfully",
        });
      };
      
      reader.onerror = () => {
        setIsUploadingImage(false);
        toast({
          title: "Upload Error",
          description: "Failed to process the image file",
          variant: "destructive",
        });
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setIsUploadingImage(false);
      toast({
        title: "Upload Error",
        description: "Failed to upload the image",
        variant: "destructive",
      });
    }
  };

  const clearImage = () => {
    setImagePreview('');
    handleChange('image', 'placeholder-dice-logo');
    
    // Clear file input
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
    
    toast({
      title: "Image Cleared",
      description: "Game image has been reset to default",
    });
  };

  const handleSave = async () => {
    try {
      const gameToSave = {
        ...editedGame,
        players: {
          min: parseInt(editedGame.minPlayers),
          max: parseInt(editedGame.maxPlayers)
        },
        playtime: parseInt(editedGame.playtime),
        complexity: parseFloat(editedGame.complexity)
      };
      
      await onSave(gameToSave);
      setIsChanged(false);
      onClose();
      
      toast({
        title: "Game updated!",
        description: `${gameToSave.name} has been updated successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error updating game",
        description: "Failed to update the game. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${game.name}"? This action cannot be undone.`)) {
      try {
        await onDelete(game.id);
        onClose();
        
        toast({
          title: "Game deleted",
          description: `${game.name} has been removed from your collection.`,
        });
      } catch (error) {
        toast({
          title: "Error deleting game",
          description: "Failed to delete the game. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const resetChanges = () => {
    // Check if game should be marked as co-op based on either field or tags
    const shouldBeCoOp = game.isCoOp || hasCoOpTags(game.tags);
    
    setEditedGame({
      ...game,
      minPlayers: game.players?.min || 1,
      maxPlayers: game.players?.max || 4,
      tags: [...(game.tags || [])],
      isCoOp: shouldBeCoOp // Set checkbox based on field OR tags
    });
    setIsChanged(false);
  };

  if (!game) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-800 dark:bg-gray-800 border-gray-600 dark:border-gray-600 duration-100 w-[95vw] md:w-full p-4 md:p-6"
      >
        <DialogHeader className="space-y-2 md:space-y-3">
          <DialogTitle className="text-lg md:text-xl font-semibold text-white dark:text-white">
            Edit Game
          </DialogTitle>
          {isChanged && (
            <div className="text-sm text-amber-300 dark:text-amber-300 bg-amber-900/30 dark:bg-amber-900/30 border border-amber-600 dark:border-amber-600 rounded-md px-3 py-2">
              You have unsaved changes
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4 md:space-y-6 pt-2 md:pt-4">
          {/* Basic Information & Tags - Combined */}
          <Card className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600">
            <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
              <h3 className="text-lg font-medium text-white dark:text-white mb-3">Basic Information & Tags</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300 dark:text-gray-300">Game Name</Label>
                  <Input
                    value={editedGame.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                    placeholder="Enter game name"
                  />
                </div>

                {/* BGG Data Fetch Section - Only show if authenticated */}
                {isAuthenticated ? (
                  <div className="col-span-2 space-y-3 pt-3 border-t border-gray-600 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                      <ExternalLink size={16} className="text-orange-400" />
                      <Label className="text-gray-300 dark:text-gray-300 font-medium">
                        Fetch from BoardGameGeek
                      </Label>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-400">
                      Enter a BoardGameGeek URL or game ID to auto-populate game details
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={bggUrl}
                        onChange={(e) => setBggUrl(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !isLoadingBGG && bggUrl.trim() && handleFetchBGGData()}
                        className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        placeholder="https://boardgamegeek.com/boardgame/3955/bang or just 3955"
                        disabled={isLoadingBGG}
                      />
                      <Button
                        onClick={handleFetchBGGData}
                        disabled={isLoadingBGG || !bggUrl.trim()}
                        variant="outline"
                        size="sm"
                        className="border-orange-500 dark:border-orange-500 text-orange-400 dark:text-orange-400 hover:bg-orange-600 dark:hover:bg-orange-600 hover:text-white dark:hover:text-white whitespace-nowrap px-3"
                      >
                        {isLoadingBGG ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent mr-2" />
                            Loading
                          </>
                        ) : (
                          <>
                            <Download size={14} className="mr-2" />
                            Fetch Data
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="col-span-2 space-y-3 pt-3 border-t border-gray-600 dark:border-gray-600 opacity-60">
                    <div className="flex items-center gap-2">
                      <ExternalLink size={16} className="text-gray-500" />
                      <Label className="text-gray-500 dark:text-gray-500 font-medium">
                        Fetch from BoardGameGeek
                      </Label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      🔒 PIN authentication required to access BoardGameGeek data fetching
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-gray-300 dark:text-gray-300">Min Players</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={editedGame.minPlayers || 1}
                    onChange={(e) => handleChange('minPlayers', e.target.value)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 dark:text-gray-300">Max Players</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={editedGame.maxPlayers || 4}
                    onChange={(e) => handleChange('maxPlayers', e.target.value)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 dark:text-gray-300">Playtime (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editedGame.playtime || 30}
                    onChange={(e) => handleChange('playtime', e.target.value)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 dark:text-gray-300 flex items-center gap-2">
                    Complexity 
                    <span className="text-sm font-normal text-gray-400 dark:text-gray-400">({Math.floor((editedGame.complexity || 2.0) * 10) / 10})</span>
                  </Label>
                  <div className="mt-2 px-2">
                    <Slider
                      value={[editedGame.complexity || 2.0]}
                      onValueChange={(value) => handleChange('complexity', value[0])}
                      max={5}
                      min={1}
                      step={0.1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-400 dark:text-gray-400 mt-1">
                      <span>1 (Simple)</span>
                      <span>5 (Complex)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <Label className="text-gray-300 dark:text-gray-300">Description</Label>
                <Textarea
                  value={editedGame.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                  placeholder="Brief description of the game"
                  rows={3}
                />
              </div>

              {/* Tags Section - Integrated */}
              <div className="col-span-2 space-y-3 pt-2 border-t border-gray-600 dark:border-gray-600">
                <Label className="text-gray-300 dark:text-gray-300">Tags</Label>
                
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                    placeholder="Add a tag"
                  />
                  <Button 
                    onClick={handleAddTag}
                    variant="outline" 
                    size="icon"
                    className="border-gray-500 dark:border-gray-500 text-gray-300 dark:text-gray-300 hover:bg-orange-600 dark:hover:bg-orange-600 hover:border-orange-500 dark:hover:border-orange-500 hover:text-white dark:hover:text-white"
                  >
                    <Plus size={16} />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {editedGame.tags?.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="bg-orange-600 dark:bg-orange-600 text-white dark:text-white hover:bg-orange-700 dark:hover:bg-orange-700 pr-1 pl-3 py-1 border border-orange-500 dark:border-orange-500"
                    >
                      {tag}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 h-4 w-4 p-0 hover:bg-orange-700 dark:hover:bg-orange-700 text-white dark:text-white hover:text-orange-100 dark:hover:text-orange-100"
                      >
                        <X size={12} />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Status Options - Single Line */}
              <div className="col-span-2 space-y-2 pt-2 border-t border-gray-600 dark:border-gray-600">
                <Label className="text-gray-300 dark:text-gray-300">Status & Options</Label>
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="coop"
                      checked={editedGame.isCoOp || false}
                      onCheckedChange={(checked) => handleChange('isCoOp', checked)}
                      className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <Label htmlFor="coop" className="text-gray-300 dark:text-gray-300">Co-op</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="owned"
                      checked={editedGame.owned || false}
                      onCheckedChange={(checked) => {
                        handleChange('owned', checked);
                        if (checked) {
                          handleChange('wishlist', false);
                          handleChange('try', false);
                        }
                      }}
                      className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <Label htmlFor="owned" className="text-gray-300 dark:text-gray-300">Owned</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="wishlist"
                      checked={editedGame.wishlist || false}
                      onCheckedChange={(checked) => {
                        handleChange('wishlist', checked);
                        if (checked) {
                          handleChange('owned', false);
                          handleChange('try', false);
                        }
                      }}
                      className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <Label htmlFor="wishlist" className="text-gray-300 dark:text-gray-300">Wishlist</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="try"
                      checked={editedGame.try || false}
                      onCheckedChange={(checked) => {
                        handleChange('try', checked);
                        if (checked) {
                          handleChange('owned', false);
                          handleChange('wishlist', false);
                        }
                      }}
                      className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <Label htmlFor="try" className="text-gray-300 dark:text-gray-300">Want to Try</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Image Upload */}
          <Card className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600">
            <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
              <h3 className="text-base md:text-lg font-medium text-white dark:text-white mb-2 md:mb-3">Game Image</h3>
              
              <div className="space-y-4">
                {/* Current Image Preview */}
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 border-2 border-dashed border-gray-500 rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
                    {imagePreview ? (
                      <img 
                        src={imagePreview} 
                        alt="Game preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : editedGame.image && !editedGame.image.includes('placeholder') ? (
                      <img 
                        src={editedGame.image} 
                        alt="Game image" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 text-xs text-center p-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded flex items-center justify-center text-sm font-bold text-orange-900 transform rotate-12 mb-1">
                          Z
                        </div>
                        <span>Default</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div>
                      <Label className="text-gray-300 dark:text-gray-300 text-sm">Upload New Image</Label>
                      <p className="text-xs text-gray-400 mt-1">
                        Upload a custom image for this game (max 2MB, JPEG/PNG/WebP/GIF)
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploadingImage}
                      />
                      
                      <Button
                        type="button"
                        onClick={() => document.getElementById('image-upload').click()}
                        disabled={isUploadingImage}
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-400 hover:bg-blue-600 hover:text-white"
                      >
                        {isUploadingImage ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent mr-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Plus size={14} className="mr-2" />
                            Upload Image
                          </>
                        )}
                      </Button>
                      
                      {(imagePreview || (editedGame.image && !editedGame.image.includes('placeholder'))) && (
                        <Button
                          type="button"
                          onClick={clearImage}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-400 hover:bg-red-600 hover:text-white"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Clear Image
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-400 flex items-start gap-2">
                    <span className="text-blue-400">ℹ️</span>
                    <span>
                      <strong>Image Guidelines:</strong> For best results, use square images (1:1 aspect ratio). 
                      Images are stored securely in the database and will be available offline. 
                      If no custom image is uploaded, the default Z-dice logo will be used.
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600">
            <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
              <h3 className="text-base md:text-lg font-medium text-white dark:text-white">Metadata</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 dark:text-gray-300">Rating</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Star size={16} className="text-orange-500" />
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      step="0.1"
                      value={editedGame.rating || ''}
                      onChange={(e) => handleChange('rating', e.target.value ? parseFloat(e.target.value) : null)}
                      className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                      placeholder="1.0 - 10.0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 dark:text-gray-300">Play Count</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editedGame.playCount || 0}
                    onChange={(e) => handleChange('playCount', parseInt(e.target.value) || 0)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-300 dark:text-gray-300">Notes</Label>
                  <Textarea
                    value={editedGame.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    className="bg-gray-600 dark:bg-gray-600 border-gray-500 dark:border-gray-500 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 mt-1 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                    placeholder="Personal notes about this game"
                    rows={2}
                  />
                </div>
              </div>

              {/* Status options moved to Basic Information section above */}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row justify-between gap-3 pt-3 md:pt-4 border-t border-gray-600 dark:border-gray-600">
            {/* Delete button - full width on mobile */}
            <Button
              onClick={handleDelete}
              variant="outline"
              className="w-full md:w-auto border-red-500 dark:border-red-500 text-red-400 dark:text-red-400 hover:bg-red-600 dark:hover:bg-red-600 hover:text-white dark:hover:text-white min-h-[44px]"
            >
              <Trash2 size={16} className="mr-2" />
              Delete Game
            </Button>
            
            {/* Action buttons - stack on mobile, inline on desktop */}
            <div className="flex flex-col md:flex-row gap-2 md:gap-3">
              {isChanged && (
                <Button
                  onClick={resetChanges}
                  variant="outline"
                  className="w-full md:w-auto border-gray-500 dark:border-gray-500 text-gray-300 dark:text-gray-300 hover:bg-gray-600 dark:hover:bg-gray-600 min-h-[44px]"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset Changes
                </Button>
              )}
              
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full md:w-auto border-gray-500 dark:border-gray-500 text-gray-300 dark:text-gray-300 hover:bg-gray-600 dark:hover:bg-gray-600 min-h-[44px]"
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={!editedGame.name?.trim() || !isChanged}
                className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white min-h-[44px]"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

GameEditModal.displayName = 'GameEditModal';

export default GameEditModal;